import axios from 'axios'
import { InboxContent, InboxService } from '../../inbox/InboxService'
import { OperationContext, OperationSpec } from './OperationContext'
import { RunnableOperation } from './RunnableOperation'

/**
 * Scarica un contenuto della SecureInbox (smartshare).
 *
 * A differenza di RunnableFileDownload il download viene eseguito qui dentro e
 * non dal chiamante: serve perché l'operazione compaia in "Operazioni in corso"
 * con progress reale e possibilità di annullamento.
 *
 * Il blob risultante viene esposto su `blob`: il chiamante lo recupera nella
 * callback `operationComplete:<operationId>` e decide cosa farne (salvataggio su
 * disco compreso, che resta responsabilità dell'app).
 */
export class RunnableInboxDownload extends RunnableOperation {
  public blob?: Blob
  private abortController = new AbortController()

  constructor(userOpSpec: OperationSpec) {
    super(userOpSpec)
  }

  private get inboxFile(): InboxContent {
    return this.userOpSpec.info as InboxContent
  }

  async defineRequest(context: OperationContext): Promise<void> {
    await super.defineRequest(context)
    if (this.aborted) {
      throw new Error('Operation aborted')
    }

    context.declareRequestSpec = {
      operationType: 'inbox.download',
      pathName: this.userOpSpec.from,
    }
  }

  async execute(context: OperationContext): Promise<void> {
    if (this.aborted) {
      throw new Error('Operation aborted')
    }
    this.setStatus('running')

    const { download_url } = await InboxService.downloadFile(this.inboxFile)

    const response = await axios.get(download_url, {
      responseType: 'blob',
      signal: this.abortController.signal,
      onDownloadProgress: (progressEvent) => {
        if (!progressEvent.total) {
          return
        }
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        // setProgress(100) marca l'operazione come completata: l'ultimo step lo
        // lascia fare a processQueue, quando anche il blob è disponibile.
        if (percentCompleted < 100) {
          this.setProgress(percentCompleted)
        }
      },
    })

    this.blob = response.data as Blob
  }

  abort(): void {
    this.aborted = true
    this.abortController.abort()
  }
}
